import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getVehiclesByOwner, getAllVehicles, updateVehicle, deleteVehicle } from '../api/services/vehiclesService';
import {
  reverseGeocode,
  getRoute,
  routingCostingForVehicleType,
  haversineKm,
  positionOrNull,
  SAME_PLACE_THRESHOLD_KM,
  RouteDetails,
  formatKm,
  COSTING_LABELS,
} from '../lib/melqarto';
import OverviewMap from '../components/OverviewMap';
import { getAllUsersWithToken } from '../api/services/usersService';
import { getRentalStateByVehicle, VehicleRentalState, VehicleRentalStatus } from '../api/services/adminRentalsService';
import VehicleEditor, { OwnerOption } from '../features/management/components/VehicleEditor';
import { can } from '../lib/permissions';
import { Avatar, Card, FilterChip, ICONS, LineIcon, LinkButton, SectionLabel, StatusPill, Tone, VehicleThumb } from '../components/ui/dg';
import {
  VEHICLE_TYPE_META as TYPE_META,
  VEHICLE_TYPE_ORDER as TYPE_ORDER,
  VehicleTypeKey as VehicleType,
} from '../lib/vehicleCatalog';

const PRIMARY = '#4F46E5'; // indigo-600 — déjà la couleur du back-office (sidebar, spinners)

/**
 * État de la location qui compte pour un véhicule (en cours, acceptée,
 * en attente, terminée, annulée) : pastille et pastille de tarif du même ton.
 */
const RENTAL_STATE: Record<VehicleRentalState, { label: string; tone: Tone; dateHint: string }> = {
  ongoing: { label: 'En cours', tone: 'green', dateHint: 'Retour prévu' },
  accepted: { label: 'À venir', tone: 'indigo', dateHint: 'Prise en charge' },
  pending: { label: 'Demande', tone: 'amber', dateHint: 'Prise en charge demandée' },
  completed: { label: 'Terminé', tone: 'grey', dateHint: 'Rendu' },
  cancelled: { label: 'Annulé', tone: 'red', dateHint: 'Annulé' }
};

/** « Aujourd'hui, 10:00 », « Demain, 14:00 », « Hier, 18:00 », sinon « 12 oct., 09:00 ». */
const relativeDay = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  const time = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((startOf(date) - startOf(new Date())) / 86400000);
  if (diff === 0) return `Aujourd’hui, ${time}`;
  if (diff === 1) return `Demain, ${time}`;
  if (diff === -1) return `Hier, ${time}`;
  return `${date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}, ${time}`;
};

const FleetView = () => {
  const { token, user } = useAuth();
  const isStaff = user?.role === 'admin' || user?.role === 'moderator';
  // Trois droits distincts, comme côté API : corriger une annonce relève de la
  // modération, la supprimer ou la transférer engage la plateforme. Masquer un
  // bouton ne protège rien — c'est `loadVehicleForWrite` qui refuse.
  const canEditVehicle = can(user?.role, 'vehicle.edit');
  const canDeleteVehicle = can(user?.role, 'vehicle.delete');
  const canReassignVehicle = can(user?.role, 'vehicle.reassign');
  const [userVehicles, setUserVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // Une liste vide parce que l'API n'a pas répondu et une flotte vraiment
  // vide ne doivent pas se ressembler : la première s'est affichée comme
  // « Ajoutez votre premier véhicule » pendant une panne, et l'admin a cru
  // ses annonces disparues.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  // `open` sans `vehicle` = création ; avec `vehicle` = modification. Un seul
  // état pour les deux, l'éditeur étant le même formulaire dans les deux cas.
  const [editor, setEditor] = useState<{ open: boolean; vehicle: any | null }>({ open: false, vehicle: null });
  const [cities, setCities] = useState<{ [key: string]: string }>({});
  const [ownersById, setOwnersById] = useState<{ [key: string]: string }>({});
  // La même liste de comptes, sous la forme attendue par le sélecteur de
  // propriétaire de l'éditeur : la carte id -> nom ci-dessus ne sait pas
  // afficher un email pour départager deux homonymes.
  const [owners, setOwners] = useState<OwnerOption[]>([]);
  // L'état du chargement, et pas seulement son résultat. Une liste vide parce
  // que la requête a échoué et une liste vide parce qu'il n'y a rien à montrer
  // se ressemblent dans un tableau ; dans un champ de recherche elles se
  // confondent tout à fait — l'admin lit « aucun compte ne correspond » et
  // conclut que la personne n'existe pas, alors que rien n'a été chargé.
  const [ownersStatus, setOwnersStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [ownersError, setOwnersError] = useState('');
  const [ownersReloadKey, setOwnersReloadKey] = useState(0);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | number | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | number | null>(null);
  // Même mécanique que dans l'éditeur : « Supprimer » s'arme au premier clic
  // et agit au second, sans boîte de dialogue. Retombe avec le menu.
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | number | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Préremplie par la recherche globale de la sidebar (?q=...)
  const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(window.location.search).get('q') || '');
  const [typeFilter, setTypeFilter] = useState<VehicleType | 'all'>('all');
  const [availabilityFilter, setAvailabilityFilter] = useState<'all' | 'available' | 'unavailable' | 'suspended'>('all');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [sortBy, setSortBy] = useState<'recent' | 'price_asc' | 'price_desc' | 'availability'>('recent');
  // Location qui compte, par véhicule (personnel) : pastille « En cours »,
  // « Accepté »… et sa date, comme dans la liste des trajets.
  const [rentalByVehicle, setRentalByVehicle] = useState<Record<string, VehicleRentalStatus>>({});
  useEffect(() => {
    if (!isStaff || !token) return;
    getRentalStateByVehicle(token)
      .then((rows) => setRentalByVehicle(Object.fromEntries(rows.map((r) => [String(r.vehicle_id), r]))))
      .catch(() => setRentalByVehicle({}));
  }, [isStaff, token, loadAttempt]);

  // Charger les véhicules : tous les propriétaires pour le staff (admin/modérateur),
  // seulement les siens pour un utilisateur normal
  useEffect(() => {
    const loadUserVehicles = async () => {
      try {
        setLoading(true);
        setLoadError(null);

        const vehicles = isStaff
          ? await getAllVehicles(token ?? "")
          : await getVehiclesByOwner(
              ((user as any)?.id || (user as any)?.id_user || (user as any)?.user_id || (user as any)?.userId || '').toString(),
              token ?? ""
            );

        setUserVehicles(Array.isArray(vehicles) ? vehicles : []);
      } catch (error: any) {
        console.error('❌ Erreur lors du chargement des véhicules:', error);
        setUserVehicles([]);
        const message = String(error?.message || '');
        setLoadError(
          message.includes('401') ? 'Votre session a expiré : reconnectez-vous.'
            : message.includes('403') ? "Votre rôle ne donne pas accès à la flotte."
            : /^Erreur \d+/.test(message) ? `L'API a répondu une erreur (${message.replace(/^Erreur (\d+).*/, '$1')}).`
            : "L'API ne répond pas. Vérifiez qu'elle est en ligne, puis réessayez."
        );
      } finally {
        setLoading(false);
      }
    };

    loadUserVehicles();
  }, [token, user, isStaff, loadAttempt]);

  // Charger les propriétaires (staff uniquement — pour afficher un nom plutôt
  // qu'un owner_id dans la liste)
  useEffect(() => {
    if (!isStaff || !token) {
      setOwnersStatus('ready');
      return;
    }
    setOwnersStatus('loading');
    setOwnersError('');
    getAllUsersWithToken(token)
      .then((users: any[]) => {
        const map: { [key: string]: string } = {};
        const list: OwnerOption[] = [];
        for (const u of Array.isArray(users) ? users : []) {
          const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
          map[u.id_user] = name || u.email || 'Propriétaire';
          list.push({ id: u.id_user, name: name || u.email || 'Compte sans nom', email: u.email });
        }
        setOwnersById(map);
        // Trié par nom : le sélecteur de propriétaire cherche dans cette liste,
        // et l'ordre d'inscription n'aide personne.
        setOwners(list.sort((a, b) => a.name.localeCompare(b.name, 'fr')));
        setOwnersStatus('ready');
      })
      .catch((error) => {
        // `/api/users` exige `auth` + `requireStaff`, là où `/api/vehicles`
        // passe par `optionalAuth` et répond même sans jeton valide. Une
        // session expirée donne donc exactement cet écran : les véhicules
        // s'affichent, la liste des comptes est vide, et le champ de recherche
        // du propriétaire répond « aucun compte ne correspond » à un email qui
        // existe bel et bien. L'échec doit se voir.
        console.error('❌ Erreur lors du chargement des comptes:', error);
        const message = String(error?.message || '');
        setOwnersStatus('error');
        setOwnersError(
          message.includes('401')
            ? 'Votre session a expiré : reconnectez-vous pour transférer une annonce.'
            : message.includes('403')
              ? "Votre rôle ne donne pas accès à la liste des comptes."
              : "La liste des comptes n'a pas pu être chargée."
        );
      });
  }, [isStaff, token, ownersReloadKey]);

  /**
   * Ville de chaque annonce, par Melqarto — quelques requêtes de front, sans
   * la seconde d'attente que le Nominatim public imposait entre deux appels
   * (une flotte de soixante annonces mettait une minute à se nommer).
   * Une ville inconnue est mémorisée vide, pour ne pas la redemander à
   * chaque rendu.
   */
  const loadVehicleCities = async (vehicles: any[]) => {
    const pending = Array.from(new Set(
      vehicles
        .filter((v) => v.latitude && v.longitude)
        .map((v) => `${v.latitude},${v.longitude}`)
    )).filter((key) => !(key in cities));
    if (pending.length === 0) return;

    const queue = [...pending];
    const worker = async () => {
      while (queue.length > 0) {
        const key = queue.shift() as string;
        const [lat, lng] = key.split(',').map(Number);
        const address = await reverseGeocode(lat, lng);
        const city = address?.city || '';
        setCities((prev) => (prev[key] === city ? prev : { ...prev, [key]: city }));
      }
    };
    await Promise.all(Array.from({ length: Math.min(4, queue.length) }, worker));
  };

  useEffect(() => {
    if (userVehicles.length > 0) {
      loadVehicleCities(userVehicles).catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userVehicles]);

  const getVehicleTypeInFrench = (vehicleType: string) => TYPE_META[vehicleType as VehicleType]?.singular || 'Véhicule';
  const getVehicleColor = (vehicleType: string) => TYPE_META[vehicleType as VehicleType]?.color || PRIMARY;

  const openAddVehicle = () => setEditor({ open: true, vehicle: null });

  const openEditVehicle = (vehicle: any) => {
    setEditor({ open: true, vehicle });
    setOpenMenuId(null);
  };

  /**
   * Insère ou remplace l'annonce que l'éditeur vient d'enregistrer.
   *
   * Remplacement plutôt que rechargement complet de la liste : la Flotte
   * géocode chaque position pour afficher une ville, avec une seconde d'attente
   * entre deux appels pour ne pas se faire refuser par le service. Tout
   * recharger après chaque enregistrement rendrait l'écran inutilisable.
   */
  const handleSaved = (saved: any) => {
    const savedId = saved.id_vehicle || saved.id;
    setUserVehicles(prev => {
      const exists = prev.some(v => (v.id_vehicle || v.id) === savedId);
      return exists
        ? prev.map(v => ((v.id_vehicle || v.id) === savedId ? saved : v))
        : [...prev, saved];
    });
  };

  // Le backend renvoie 401 quand le token a expiré (durée de vie de 24h) ou est
  // invalide. Sans ça, l'échec était silencieux — juste un console.error — et
  // l'utilisateur voyait "rien ne se passe" en cliquant Modifier, sans aucun
  // indice que c'était sa session qui avait expiré.
  //
  // Le 403 était traité comme un 401 et DÉCONNECTAIT : c'était sans conséquence
  // tant que les routes véhicule ne vérifiaient aucun droit, donc tant que le
  // seul 403 possible venait d'un jeton refusé. Depuis qu'elles distinguent le
  // propriétaire, le modérateur et l'admin, un modérateur qui tente un
  // transfert reçoit un 403 parfaitement légitime — le jeter dehors serait une
  // réponse absurde à « vous n'avez pas ce droit ».
  const handleActionError = (error: any, action: string) => {
    console.error(`❌ Erreur lors de ${action}:`, error);
    const message = String(error?.message || '');

    // Le 401 est traité une fois pour toutes par AuthContext, qui observe les
    // réponses de l'API et redirige vers la connexion avec le motif affiché.
    // Une alerte ici viendrait par-dessus cette redirection.
    if (message.includes('401')) return;

    // Le service remonte désormais le message de l'API après le code : c'est lui
    // qui dit quoi corriger (tarif hors limites, propriétaire introuvable,
    // droit manquant), là où « Réessayez dans un instant » envoyait réessayer
    // une action qui échouera à l'identique.
    const detail = message.replace(/^Erreur \d+:\s*/, '').trim();
    alert(detail && detail !== message ? detail : `Échec : ${action} n'a pas pu aboutir. Réessayez dans un instant.`);
  };

  const toggleSuspendVehicle = async (vehicle: any) => {
    const vId = vehicle.id_vehicle || vehicle.id;
    const nextSuspended = !vehicle.is_suspended;
    let reason: string | null = null;
    if (nextSuspended) {
      reason = window.prompt('Motif de la suspension (optionnel) :') || null;
    }
    const vehicleData = {
      serial_number: vehicle.serial_number,
      vehicle_type: vehicle.vehicle_type,
      brand: vehicle.brand,
      model: vehicle.model,
      year: vehicle.year,
      is_platform_owned: vehicle.is_platform_owned,
      is_available: nextSuspended ? false : vehicle.is_available,
      hourly_rate: vehicle.hourly_rate,
      latitude: vehicle.latitude,
      longitude: vehicle.longitude,
      owner_id: vehicle.owner_id,
      is_suspended: nextSuspended,
      suspension_reason: nextSuspended ? reason : null,
    };
    try {
      const updated = await updateVehicle(vId, vehicleData, token ?? "");
      // Fusion et non remplacement : `RETURNING *` ne rapporte ni les photos ni
      // la note, qui ne sont pas des colonnes de "Vehicle". Les écraser ferait
      // disparaître les photos de l'annonce à l'écran jusqu'au rechargement.
      setUserVehicles(prev => prev.map(v => (v.id_vehicle || v.id) === vId ? { ...v, ...updated } : v));
    } catch (error) {
      handleActionError(error, nextSuspended ? 'la suspension du véhicule' : 'la réactivation du véhicule');
    }
    setOpenMenuId(null);
  };

  const deleteVehicleHandler = async (vehicleId: string | number) => {
    try {
      const result = await deleteVehicle(String(vehicleId), token ?? "");
      // Le nombre de locations annulées et de locataires prévenus est dans la
      // réponse et au journal d'audit ; pas de fenêtre pour le répéter.
      void result;
      setUserVehicles(prev => prev.filter(v => v.id_vehicle !== vehicleId && v.id !== vehicleId));
      // La suppression peut venir de l'éditeur ouvert sur cette annonce : le
      // laisser ouvert sur un véhicule qui n'existe plus ferait échouer le
      // prochain enregistrement en 404.
      setEditor(prev => (prev.vehicle && (prev.vehicle.id_vehicle || prev.vehicle.id) === vehicleId
        ? { open: false, vehicle: null }
        : prev));
    } catch (error) {
      handleActionError(error, 'la suppression du véhicule');
    } finally {
      setOpenMenuId(null);
    }
  };

  const zones = useMemo(
    () => Array.from(new Set(Object.values(cities))).filter(Boolean).sort(),
    [cities]
  );

  const cityFor = (vehicle: any) => {
    const key = vehicle.latitude && vehicle.longitude ? `${vehicle.latitude},${vehicle.longitude}` : null;
    return key ? cities[key] : null;
  };

  // Le propriétaire fait foi, pas le drapeau « plateforme » : des annonces
  // transférées gardaient le drapeau à vrai et s'affichaient « Plateforme »
  // chez leur nouveau propriétaire (corrigé côté API, migration 043, mais la
  // liste ne doit pas dépendre de la propreté de la base pour dire la vérité).
  const ownerNameFor = (vehicle: any) => {
    if (!vehicle.owner_id) return 'Plateforme';
    return ownersById[vehicle.owner_id] || null;
  };

  const filteredVehicles = useMemo(() => {
    const result = userVehicles.filter(vehicle => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm.trim() ||
        vehicle.brand?.toLowerCase().includes(searchLower) ||
        vehicle.model?.toLowerCase().includes(searchLower) ||
        vehicle.serial_number?.toLowerCase().includes(searchLower) ||
        getVehicleTypeInFrench(vehicle.vehicle_type).toLowerCase().includes(searchLower);

      const matchesType = typeFilter === 'all' || vehicle.vehicle_type === typeFilter;

      const matchesAvailability =
        availabilityFilter === 'all' ||
        (availabilityFilter === 'available' && vehicle.is_available && !vehicle.is_suspended) ||
        (availabilityFilter === 'unavailable' && !vehicle.is_available && !vehicle.is_suspended) ||
        (availabilityFilter === 'suspended' && vehicle.is_suspended);

      const matchesZone = zoneFilter === 'all' || cityFor(vehicle) === zoneFilter;

      return matchesSearch && matchesType && matchesAvailability && matchesZone;
    });

    const sorted = [...result];
    switch (sortBy) {
      case 'price_asc':
        sorted.sort((a, b) => (Number(a.hourly_rate) || 0) - (Number(b.hourly_rate) || 0));
        break;
      case 'price_desc':
        sorted.sort((a, b) => (Number(b.hourly_rate) || 0) - (Number(a.hourly_rate) || 0));
        break;
      case 'availability':
        sorted.sort((a, b) => (b.is_available ? 1 : 0) - (a.is_available ? 1 : 0));
        break;
      case 'recent':
      default:
        sorted.sort((a, b) => new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime());
        break;
    }
    return sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userVehicles, searchTerm, typeFilter, availabilityFilter, zoneFilter, sortBy, cities]);

  const selectedVehicle = useMemo(
    () => filteredVehicles.find(v => (v.id_vehicle || v.id) === selectedVehicleId) || null,
    [filteredVehicles, selectedVehicleId]
  );

  // Itinéraire prise en charge → restitution de l'annonce sélectionnée, par
  // Melqarto, quand les deux lieux diffèrent. Le trait droit ne dit rien de
  // ce que le locataire fera vraiment ; le tracé et sa longueur, si.
  const [selectedRoute, setSelectedRoute] = useState<(RouteDetails & { end: { lat: number; lng: number } }) | null>(null);
  useEffect(() => {
    setSelectedRoute(null);
    if (!selectedVehicle) return;
    const pickup = positionOrNull(selectedVehicle.latitude, selectedVehicle.longitude);
    const dropoff = positionOrNull(selectedVehicle.return_latitude, selectedVehicle.return_longitude);
    const costing = routingCostingForVehicleType(selectedVehicle.vehicle_type);
    if (!pickup || !dropoff || !costing || haversineKm(pickup, dropoff) < SAME_PLACE_THRESHOLD_KM) return;

    const controller = new AbortController();
    getRoute(pickup, dropoff, costing, controller.signal)
      .then((route) => setSelectedRoute({ ...route, end: dropoff }))
      // Pas d'itinéraire (réseau, point hors route) : la carte garde juste le marqueur.
      .catch(() => {});
    return () => controller.abort();
  }, [selectedVehicle]);

  // L'état de l'annonce, comme sur la carte de l'app.
  const listingState = (v: any): { label: string; tone: Tone } =>
    v.is_suspended ? { label: 'Suspendue', tone: 'red' }
      : v.owner_reachable === false ? { label: 'Sans numéro', tone: 'red' }
      : v.owner_fees_blocked ? { label: 'Frais dus', tone: 'red' }
      : !v.is_available ? { label: 'En pause', tone: 'grey' }
      : { label: 'En ligne', tone: 'green' };
  // `hourly_rate` porte le prix du JOUR des anciennes annonces, jamais un prix horaire.
  const pricesOf = (v: any): { value: string; unit: string }[] => {
    const daily = Number(v.daily_rate) || (Number(v.price_per_hour) ? 0 : Number(v.hourly_rate) || 0);
    const hourly = Number(v.price_per_hour) || 0;
    return [
      ...(daily > 0 ? [{ value: `${daily.toFixed(0)} DT`, unit: 'par jour' }] : []),
      ...(hourly > 0 ? [{ value: `${hourly.toFixed(0)} DT`, unit: 'par heure' }] : []),
    ];
  };
  const mapVehicles = useMemo(() => filteredVehicles.map(v => ({
    id: v.id_vehicle || v.id,
    title: v.brand && v.model ? `${v.brand} ${v.model}` : v.serial_number || 'Véhicule',
    type: v.vehicle_type,
    latitude: Number(v.latitude),
    longitude: Number(v.longitude),
    is_available: v.is_available,
    hourly_rate: v.hourly_rate,
    year: v.year,
    city: cityFor(v),
    isPlatformOwned: v.is_platform_owned,
    // Ce que montre le popup de la carte : photo, plaque, type en français,
    // l'état de l'annonce (le même que dans la liste) et les deux tarifs.
    raw: v,
    plate: v.serial_number || null,
    typeLabel: getVehicleTypeInFrench(v.vehicle_type),
    state: listingState(v),
    prices: pricesOf(v),
    ownerName: isStaff ? ownerNameFor(v) : null,
    // `cities` et non `cityFor` : la fonction est recréée à chaque rendu, la
    // lister ferait recalculer la liste des marqueurs à chaque frappe dans la
    // recherche. Même arbitrage que le useMemo voisin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [filteredVehicles, cities, ownersById, isStaff]);

  const counts = {
    all: userVehicles.length,
    available: userVehicles.filter((v) => v.is_available && !v.is_suspended).length,
    unavailable: userVehicles.filter((v) => !v.is_available && !v.is_suspended).length,
    suspended: userVehicles.filter((v) => v.is_suspended).length,
  };
  const COLS = 'lg:grid-cols-[2.1fr_1.4fr_0.8fr_1fr_1.2fr_32px]';
  const priceOf = (v: any) => {
    const daily = Number(v.daily_rate) || 0;
    const hourly = Number(v.hourly_rate) || 0;
    return daily > 0 ? `${daily.toFixed(0)} DT` : hourly > 0 ? `${hourly.toFixed(0)} DT/h` : '—';
  };
  const titleOf = (v: any) => [v.brand, v.model].map((x: any) => (x || '').trim()).filter(Boolean).join(' ') || v.serial_number || 'Véhicule';
  const filtersActive = zoneFilter !== 'all' || typeFilter !== 'all' || sortBy !== 'recent';

  return (
    <main className="flex min-h-0 flex-1 gap-4 overflow-hidden bg-[#F7F7FB] px-4 pb-6 pt-1 lg:px-7">
      <div className="flex min-w-0 flex-1 flex-col gap-3.5 overflow-y-auto [&>*]:shrink-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1.5">
            {([
              ['all', 'Toutes', counts.all],
              ['available', 'En ligne', counts.available],
              ['unavailable', 'En pause', counts.unavailable],
              ['suspended', 'Suspendues', counts.suspended],
            ] as const).map(([id, label, n]) => (
              <FilterChip key={id} active={availabilityFilter === id} count={loading ? undefined : n}
                countTone={id === 'suspended' && n > 0 ? 'amber' : undefined}
                onClick={() => setAvailabilityFilter(id as any)}>{label}</FilterChip>
            ))}
          </div>
          <span className="flex-1" />
          <div className="relative w-full sm:w-[200px]">
            <LineIcon d={ICONS.search} size={15} color="#9CA3AF" className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input type="search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Marque, modèle, plaque…" aria-label="Rechercher un véhicule"
              className="h-8 w-full rounded-full border border-[#E5E7EB] bg-white pl-9 pr-3 text-[12.5px] focus:border-[#C7C4F6] focus:outline-none focus:ring-4 focus:ring-[#4F46E5]/10" />
          </div>
          <FilterChip active={filtersOpen || filtersActive} onClick={() => setFiltersOpen((o) => !o)}
            icon={<LineIcon d={ICONS.filter} size={14} color={filtersOpen || filtersActive ? '#fff' : '#4B5563'} />}>Filtrer</FilterChip>
          <button type="button" onClick={openAddVehicle} title="Ajouter un véhicule" aria-label="Ajouter un véhicule"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#4F46E5] text-white hover:bg-[#4338CA]">
            <LineIcon d="M12 5v14M5 12h14" size={16} color="#fff" width={2.6} />
          </button>
        </div>

        {filtersOpen && (
          <Card className="flex flex-wrap items-end gap-4 px-5 py-4">
            <label className="flex flex-col gap-1">
              <SectionLabel>Type</SectionLabel>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as any)} className="h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#4F46E5]">
                <option value="all">Tous</option>
                {TYPE_ORDER.map((t) => <option key={t} value={t}>{TYPE_META[t].plural}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <SectionLabel>Zone</SectionLabel>
              <select value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)} className="h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#4F46E5]">
                <option value="all">Toutes</option>
                {zones.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <SectionLabel>Tri</SectionLabel>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)} className="h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#4F46E5]">
                <option value="recent">Plus récentes</option>
                <option value="price_asc">Prix croissant</option>
                <option value="price_desc">Prix décroissant</option>
                <option value="availability">En ligne d’abord</option>
              </select>
            </label>
            {filtersActive && <LinkButton onClick={() => { setZoneFilter('all'); setTypeFilter('all'); setSortBy('recent'); }}>Tout effacer</LinkButton>}
          </Card>
        )}

        <Card className="overflow-hidden">
          <div className={`hidden gap-3 px-5 pb-2.5 pt-3 lg:grid ${COLS}`}>
            {['Véhicule', 'Propriétaire', 'Prix / jour', 'État', 'Location'].map((h) => <SectionLabel key={h}>{h}</SectionLabel>)}
            <span />
          </div>
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 border-t border-[#EEF0F4] px-5 py-3"><div className="h-11 w-11 animate-pulse rounded-xl bg-[#EEF0F5]" /><div className="flex-1 space-y-2"><div className="h-3.5 w-40 animate-pulse rounded bg-[#EEF0F5]" /><div className="h-3 w-24 animate-pulse rounded bg-[#EEF0F5]" /></div></div>
            ))
          ) : loadError ? (
            <div className="border-t border-[#EEF0F4] px-6 py-12 text-center">
              <p className="text-[15px] font-bold text-[#1E293B]">La flotte ne se charge pas</p>
              <p className="mt-1 text-[13px] text-[#6B7280]">{loadError}</p>
              <div className="mt-3"><LinkButton onClick={() => setLoadAttempt((n) => n + 1)}>Réessayer</LinkButton></div>
            </div>
          ) : filteredVehicles.length === 0 ? (
            <div className="border-t border-[#EEF0F4] px-6 py-12 text-center">
              <p className="text-[15px] font-bold text-[#1E293B]">{userVehicles.length === 0 ? 'Aucun véhicule' : 'Aucun résultat'}</p>
              <p className="mt-1 text-[13px] text-[#6B7280]">{userVehicles.length === 0 ? 'Ajoutez le premier véhicule de la flotte.' : 'Aucun véhicule ne correspond à ces filtres.'}</p>
            </div>
          ) : (
            filteredVehicles.map((vehicle) => {
              const vId = vehicle.id_vehicle || vehicle.id;
              const isSelected = vId === selectedVehicleId;
              const owner = isStaff ? ownerNameFor(vehicle) : null;
              const rental = rentalByVehicle[String(vId)];
              const state = rental ? RENTAL_STATE[rental.state] : null;
              const when = rental
                ? relativeDay(rental.state === 'ongoing' ? rental.end_date : rental.state === 'completed' ? (rental.completed_at || rental.end_date) : rental.state === 'cancelled' ? rental.updated_at : rental.start_date)
                : null;
              const listing = listingState(vehicle);
              return (
                <div key={vId} role="button" tabIndex={0} onClick={() => setSelectedVehicleId(vId)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setSelectedVehicleId(vId); }}
                  className={`relative grid cursor-pointer grid-cols-[1fr_auto] items-center gap-3 border-t border-[#EEF0F4] px-5 py-[11px] transition-colors ${COLS} ${
                    isSelected ? 'bg-[#F7F6FF] shadow-[inset_3px_0_0_#4F46E5]' : 'hover:bg-[#FBFBFE]'
                  }`}>
                  <span className="flex min-w-0 items-center gap-3">
                    <VehicleThumb vehicle={vehicle} dimmed={vehicle.is_suspended || !vehicle.is_available} />
                    <span className="min-w-0">
                      <span className="block truncate text-[14px] font-bold text-[#1E293B]">{titleOf(vehicle)}</span>
                      <span className="block truncate text-[12px] text-[#6B7280]">{[vehicle.serial_number || 'Sans plaque', cityFor(vehicle)].filter(Boolean).join(' · ')}</span>
                    </span>
                  </span>
                  <span className="hidden min-w-0 items-center gap-2 lg:flex">
                    {owner && <Avatar name={owner} size={28} tone="orange" />}
                    <span className="truncate text-[13px] text-[#1E293B]">{owner || '—'}</span>
                  </span>
                  <b className="hidden text-[13.5px] font-bold text-[#1E293B] lg:block">{priceOf(vehicle)}</b>
                  <span><StatusPill tone={listing.tone}>{listing.label}</StatusPill></span>
                  <span className="hidden min-w-0 lg:block" title={rental?.renter_name ? `Locataire : ${rental.renter_name}` : undefined}>
                    {state ? (
                      <>
                        <StatusPill tone={state.tone}>{state.label}</StatusPill>
                        {when && <span className="mt-0.5 block truncate text-[11.5px] text-[#9CA3AF]">{when}</span>}
                      </>
                    ) : <span className="text-[12.5px] text-[#9CA3AF]">Aucune</span>}
                  </span>
                  <span className="relative hidden lg:block" onClick={(e) => e.stopPropagation()}>
                    <button type="button" onClick={() => { setOpenMenuId(openMenuId === vId ? null : vId); setConfirmDeleteId(null); }}
                      aria-label="Actions" className="flex h-8 w-8 items-center justify-center rounded-full text-[#9CA3AF] hover:bg-[#F1F2F8] hover:text-[#1E293B]">
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg>
                    </button>
                    {openMenuId === vId && (
                      <div className="absolute right-0 top-9 z-20 w-44 rounded-[14px] border border-[#EEF0F4] bg-white p-1 shadow-[0_10px_30px_rgba(15,23,42,0.15)]">
                        <button onClick={() => openEditVehicle(vehicle)} className="w-full rounded-lg px-3 py-2 text-left text-[13px] text-[#1E293B] hover:bg-[#F7F8FB]">{canEditVehicle ? 'Modifier' : 'Consulter'}</button>
                        {canEditVehicle && (
                          <button onClick={() => toggleSuspendVehicle(vehicle)} className="w-full rounded-lg px-3 py-2 text-left text-[13px] text-[#B45309] hover:bg-[#FFF4DB]">{vehicle.is_suspended ? 'Réactiver' : 'Suspendre'}</button>
                        )}
                        {canDeleteVehicle && (
                          <button
                            onClick={() => { if (confirmDeleteId !== vId) { setConfirmDeleteId(vId); return; } setConfirmDeleteId(null); deleteVehicleHandler(vId); }}
                            className={`w-full rounded-lg px-3 py-2 text-left text-[13px] ${confirmDeleteId === vId ? 'bg-[#B91C1C] font-bold text-white' : 'text-[#B91C1C] hover:bg-[#FDECEF]'}`}>
                            {confirmDeleteId === vId ? 'Confirmer ?' : 'Supprimer'}
                          </button>
                        )}
                      </div>
                    )}
                  </span>
                </div>
              );
            })
          )}
          {!loading && !loadError && filteredVehicles.length > 0 && (
            <div className="border-t border-[#EEF0F4] px-5 py-3.5 text-[12.5px] text-[#6B7280]">{filteredVehicles.length} sur {userVehicles.length}</div>
          )}
        </Card>
      </div>

      {/* La carte, et le véhicule choisi en bas. `isolate` garde les z-index de
          Leaflet et de la carte du véhicule sous les fenêtres (éditeur). */}
      <div className="relative isolate hidden w-[380px] shrink-0 overflow-hidden rounded-[20px] border border-[#EEF0F4] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] lg:block">
        <OverviewMap
          vehicles={mapVehicles}
          mode="select"
          getMarkerColor={(v) => getVehicleColor(v.type)}
          actionLabel="Voir les détails"
          // Ouvre la fiche du véhicule, comme « Ouvrir » sous la carte : sélectionner
          // seulement ne changeait rien quand le véhicule l'était déjà.
          onVehicleClick={(v) => {
            const original = filteredVehicles.find(fv => (fv.id_vehicle || fv.id) === v.id);
            if (!original) return;
            setSelectedVehicleId(v.id);
            openEditVehicle(original);
          }}
          onVehicleSelect={selectedVehicle ? { latitude: Number(selectedVehicle.latitude), longitude: Number(selectedVehicle.longitude) } : undefined}
          route={selectedRoute ? {
            points: selectedRoute.points,
            end: selectedRoute.end,
            label: selectedRoute.distanceKm !== null
              ? `Restitution à ${formatKm(selectedRoute.distanceKm)} ${COSTING_LABELS[routingCostingForVehicleType(selectedVehicle?.vehicle_type) || 'auto']}`
              : 'Itinéraire de restitution'
          } : undefined}
        />
        {selectedVehicle && (
          <div className="absolute inset-x-3.5 bottom-3.5 z-[500] flex items-center gap-3 rounded-[18px] bg-white p-3.5 shadow-[0_8px_24px_rgba(15,23,42,0.13)]">
            <VehicleThumb vehicle={selectedVehicle} size={54} />
            <span className="min-w-0 flex-1">
              <b className="block truncate text-[14px] font-bold text-[#1E293B]">{titleOf(selectedVehicle)}</b>
              <span className="block truncate text-[12px] text-[#6B7280]">{[isStaff ? ownerNameFor(selectedVehicle) : null, cityFor(selectedVehicle), priceOf(selectedVehicle)].filter(Boolean).join(' · ')}</span>
            </span>
            <LinkButton onClick={() => openEditVehicle(selectedVehicle)}>Ouvrir</LinkButton>
          </div>
        )}
      </div>

      {editor.open && (
        <VehicleEditor
          vehicle={editor.vehicle}
          owners={owners}
          ownersStatus={ownersStatus}
          ownersError={ownersError}
          onReloadOwners={() => setOwnersReloadKey((k) => k + 1)}
          canReassign={canReassignVehicle}
          canDelete={canDeleteVehicle}
          canKeepPlate={isStaff}
          token={token ?? ''}
          currentUserId={((user as any)?.id || (user as any)?.id_user || (user as any)?.user_id || (user as any)?.userId || '').toString()}
          onClose={() => setEditor({ open: false, vehicle: null })}
          onSaved={handleSaved}
          onDeleted={deleteVehicleHandler}

          onError={handleActionError}
        />
      )}

    </main>
  );
};

export default FleetView;
